import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { TextField, Typography, Stack, Box, Autocomplete } from '@mui/material'
import BasicModal from '@/components/UI/Dialog'
import { addNewAgent } from '@/store/agents/actions'
import { getPersonas } from '@/store/common/actions'
import UserGuideTooltip from '@/components/UI/Tooltip/UserGuideTooltip'
import { KB_PATHS } from '@/routes/Support/Constants'
import { SUPPORTED_LANGUAGES } from '@/routes/Channels/Constants'

const AddAgentPopup = (props: any) => {
  const { t } = useTranslation('translation')
  const { orgLang } = props
  const dispatch = useDispatch()
  const ref = useRef(null)
  const langRef = useRef(null)
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  let defaultLang: any = {
    value: null,
    label: null,
  }
  if (orgLang) {
    defaultLang = SUPPORTED_LANGUAGES.find(l => l.value === orgLang)
  }
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLang)
  const [agentDescription, setAgentDescription] = useState('')
  const [selectedPersona, setSelectedPersona] = useState(null)
  const [personaOptions, setPersonaOptions] = useState([])

  useEffect(() => {
    if (props.show) {
      // getAgentSetting();
      const fun = async () => {
        const personas: any = await dispatch(getPersonas())
        if (personas && personas.length > 0) {
          setPersonaOptions(
            personas.map((p: any) => ({
              value: p.key,
              label: p.name,
              description: p.description,
            })),
          )
        }
      }
      fun()
    }
    return
  }, [props.show])

  const saveAgent = async () => {
    try {
      setLoading(true)
      const agent: any = await dispatch(
        addNewAgent({
          name: name,
          description: agentDescription,
          language_code: selectedLanguage.value,
        }),
      )
      if (agent) {
        await hideModal(agent)
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const onClear = () => {
    setName(null)
    setAgentDescription(null)
    setSelectedLanguage(defaultLang)
    setSelectedPersona(null)
  }

  const hideModal = (agent: any) => {
    if (agent && agent._id) {
      props.footerButtonAction(agent, selectedPersona)
      onClear()
    }
  }

  return (
    <BasicModal
      open={props.show}
      onClose={() => {
        onClear()
        props.onHide()
      }}
      title={t('Add Agent')}
      showCloseButton
      sx={{ width: '500px', height: 'auto' }}
      footerActions={[
        {
          title: t('Cancel'),
          variant: 'outlined',
          onClick: () => {
            onClear()
            props.onHide()
          },
          disabled: loading,
        },
        {
          title: t('Create'),
          variant: 'contained',
          onClick: saveAgent,
          disabled:
            !name ||
            !agentDescription ||
            !selectedLanguage?.value ||
            name.trim().length === 0 ||
            agentDescription.trim().length === 0 ||
            loading,
        },
      ]}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('Agent Name')}
            <span className="clr-red"> *</span>
          </Typography>
          <TextField
            fullWidth
            type="text"
            value={name || ''}
            onChange={(e: any) => {
              setName(e.target.value)
            }}
            placeholder={t('Agent Name')}
            size="small"
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('Agent Description')}
            <span className="clr-red"> *</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={agentDescription || ''}
            onChange={(e: any) => {
              setAgentDescription(e.target.value)
            }}
            placeholder={t('Description of Agent')}
            size="small"
          />
        </Box>

        <Box>
          <Box
            className="position-relative d-inline-flex align-items-center"
            sx={{ mb: 1 }}
            ref={langRef}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('Agent Language')}
            </Typography>
            <UserGuideTooltip
              tooltipText={t(
                'The agent language impacts the language output of the agent. This includes generating Q&As and summaries',
              )}
            />
          </Box>
          <Autocomplete
            value={selectedLanguage}
            onChange={(event, newValue) => {
              setSelectedLanguage(newValue)
            }}
            options={SUPPORTED_LANGUAGES}
            getOptionLabel={(option: any) => option?.label || ''}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.value === value?.value
            }
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t('Select Language')}
                size="small"
              />
            )}
            disableClearable
          />
        </Box>

        <Box>
          <Box
            className="agent-persona position-relative d-inline-flex align-items-center"
            sx={{ mb: 1 }}
            ref={ref}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('Agent Persona')}
            </Typography>
            <UserGuideTooltip
              tooltipText={t('Click to explore what each persona does.')}
              link={KB_PATHS.PERSONAS_LIST}
              ariaLabel={t('Learn more about AI personas')}
            />
          </Box>
          <Autocomplete
            value={selectedPersona}
            onChange={(event, newValue: any) => {
              setSelectedPersona(newValue)
            }}
            options={personaOptions}
            getOptionLabel={(option: any) => option?.label || ''}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.value === value?.value
            }
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t('Select Persona')}
                size="small"
              />
            )}
          />
        </Box>
      </Stack>
    </BasicModal>
  )
}

export default AddAgentPopup
